import crypto from 'crypto'
import { useEffect, useState } from 'react'
import { Button, Icon, TextField, Title } from 'ui'
import { z } from 'zod'
import { endpoints } from '~/constants/apiEndpoints'
import { useLoaderActionData } from '~/hooks/useLoaderActionData'
import classes from '~/styles/login/emailChangePassword.module.scss'
import { badRequest } from '~/utils/badRequest'
import { GenericActionData } from '~/utils/clientValidation'
import { RESETPASSWORD_ERROR, RESETPASSWORD_OVERTRIED, TAS_USER } from '~/utils/constant'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { inferSafeParseErrors } from '~/utils/inferSafeParseErrors'
import { validateEmail } from '~/utils/validateEmail'

import { Container, Grid, Space } from '@mantine/core'
import { ActionFunction, LoaderFunction, json, redirect } from '@remix-run/node'
import { Form, useNavigation } from '@remix-run/react'

const validateEmailAddress = (data: any) => {
  if (validateEmail(data.emailID)) {
    return true
  } else {
    return false
  }
}

export const schema = z
  .object({
    emailID: z.string().optional(),
  })
  .refine((data) => validateEmailAddress(data), {
    message: 'Please enter a valid email address.',
    path: ['emailID'],
  })

type SchemaFields = z.infer<typeof schema>
type SchemaFieldsErrors = inferSafeParseErrors<typeof schema>
type ActionData = GenericActionData<SchemaFields, SchemaFieldsErrors>

export const action: ActionFunction = async ({ request }) => {
  try {
    const formData = await request.formData()
    const fields = Object?.fromEntries(formData.entries()) as unknown as SchemaFields
    const result = schema?.safeParse(fields)

    if (!result.success) {
      return badRequest({
        fields,
        errors: result.error.flatten(),
        status: false,
        dataType: 'actionData',
      })
    }

    const body = {
      email: fields?.emailID?.trim()?.toLowerCase(),
    }
    /* eslint-disable */
    const { headers } = request
    console.log("headers?.get('X-Forwarded-For')===>", headers?.get('X-Forwarded-For'))
    const ipAddress = headers?.get('X-Forwarded-For') || ''
    const clientIP = headers.get('X-Forwarded-For')?.split(',')[0] || ''
    const proxyIP = headers.get('X-Forwarded-For')?.split(',')[1] || ''
    const resp = await fetchWrapper(
      `${endpoints.RESET_PASSWORD}/`,
      {
        headers: {
          'Content-type': 'application/json',
          'CLIENT-USER-IP': clientIP || '',
          PROXY_IP: proxyIP || '',
          X_FORWARDED_FOR: clientIP || '',
        },
        method: 'POST',
        body: JSON.stringify({ ...body }),
      },
      request,
    )

    if (resp?.status === 'OK') {
      return json({
        fields,
        emailSent: true,
        status: true,
        dataType: 'actionData',
      })
    } else {
      const CustomError = resp?.detail?.length && [RESETPASSWORD_OVERTRIED]
      let resetPasswordError = resp?.email && [RESETPASSWORD_ERROR]
      if (resp?.type === TAS_USER) {
        resetPasswordError = [resp?.email]
      }
      const randomValue = crypto.randomBytes(1)
      return badRequest({
        fields,
        errors: {
          fieldErrors: {
            emailID: resp?.email
              ? resetPasswordError
              : CustomError || ['Something went wrong. Please try again later'],
          },
        },
        status: false,
        dataType: 'actionData' + randomValue.toString('hex'),
      })
    }
  } catch (error) {
    const randomValue = crypto.randomBytes(1)
    return badRequest({
      errors: {
        fieldErrors: {
          emailID: ["We didn't find a user associated with this email address in our records."],
        },
      },
      status: false,
      dataType: 'actionData' + randomValue.toString('hex'),
    })
  }
}

export const loader: LoaderFunction = async ({ request }) => {
  return json(
    {
      fields: {
        emailID: '',
      },
      dataType: 'loaderData',
    },
    200,
  )
}

export default function ForgotPassword() {
  const loaderActionData = useLoaderActionData<ActionData>()
  const [isEmailSent, setIsEmailSent] = useState(false)
  const isSubmitting = useNavigation().state === 'submitting' || useNavigation().state === 'loading'
  useEffect(() => {
    if (loaderActionData?.dataType === 'actionData') setIsEmailSent(loaderActionData?.emailSent)
  }, [loaderActionData])
  return (
    <>
      <Container size="lg" className="topBarLogin"></Container>
      <Container size="lg">
        <Grid style={{ margin: 0 }}>
          <Grid.Col span={{ sm: 12, md: 5 }}>
            {' '}
            <Grid>
              <img
                src={'/images/LogoMPower.jpeg'}
                className="mpowerLogoLogin"
                alt="MPOWER FINANCING"
              />
            </Grid>
          </Grid.Col>
          <Grid.Col span={{ sm: 12, md: 7 }}></Grid.Col>
        </Grid>
      </Container>
      <Form method="post" autoComplete="off">
        <Container size="lg" className={classes.mainContainer}>
          <Grid>
            <Grid.Col span={{ sm: 12, md: 4 }}>
              <Title order={3} variant="blue" fontWeight="700" className={classes.headerText}>
                Forgot your password?
                <br />
                No problem.
              </Title>
            </Grid.Col>
            <Grid.Col span={{ sm: 12, md: 8 }}>
              {isEmailSent && (
                <Grid
                  pb={10}
                  px={0}
                  className={classes.resetConfirmation}
                  justify="Center"
                  align="Center"
                >
                  <Grid.Col span={{ base: 12, md: 3, lg: 3 }}>
                    <Icon name="checkEmailInbox" className={classes.resetConfirmationIcn} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 9, lg: 9 }}>
                    <Title order={5} fontWeight="700" variant="blue">
                      We sent a reset link to your email {loaderActionData?.fields?.emailID || ''}
                    </Title>
                    <Title order={6} fontWeight="400" variant="black">
                      Check your spam folder if you can't find it. Using Gmail? Check your
                      promotions tab as well.
                    </Title>
                  </Grid.Col>
                </Grid>
              )}
              <Grid pb={10} px={0}>
                <Grid.Col>
                  <Title className={classes.signInText} order={6} fontWeight="400" variant="black">
                    Enter the email associated with your account and we&apos;ll send you a link to
                    reset your password.
                  </Title>
                </Grid.Col>
              </Grid>

              {loaderActionData?.errors && (
                <>
                  <Space h={40} />
                  <Title className={classes.validationText} order={6} fontWeight="700">
                    {(loaderActionData?.errors?.fieldErrors['emailID'] || []).join(', ')}
                  </Title>
                  <Space h={50} />
                </>
              )}

              <TextField
                id={'emailID'}
                label={'Your Email ID'}
                placeholder={'Enter your email ID'}
                maxLength="80"
              />

              <Space h={40} />
              <Grid m={0} p={0} className={classes.centerDiv}>
                <Grid.Col m={0} p={0} span={{ sm: 12, md: 12 }} className="alignCenter">
                  <Button
                    variant="filled"
                    className={classes.sendRestBtnText}
                    uppercase={true}
                    type="submit"
                    loading={isSubmitting}
                    id="sendResetLink"
                  >
                    send reset password link
                  </Button>
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        </Container>
      </Form>
    </>
  )
}
